<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';

import threadRepository from '@/api/threadRepository';
import IconMoveToInbox from '@/components/icons/IconMoveToInbox.vue';
import Avatar from '@/components/ui/Avatar.vue';
import { useThreadStore } from '@/stores/thread';
import type { SentEmailRow, ThreadRow } from '@/types/thread';
import { clearBodyText, dateTimeFormatter } from '@/utils/formatters';

interface Props {
  row?: ThreadRow | SentEmailRow
}

const props = defineProps<Props>();

const { t, locale } = useI18n();
const toast = useToast();
const threadStore = useThreadStore();
const { selectedTab } = toRefs(threadStore);

const isSentTab = computed(() => selectedTab.value === 'sent');
const isTrashTab = computed(() => selectedTab.value === 'trash');
const messageDate = computed(() => dateTimeFormatter(isSentTab.value ? (props.row as SentEmailRow)?.createdAt : (props.row as ThreadRow)?.latestMailboxEmail?.createdAt, locale.value));
const messageBodyText = computed(() => clearBodyText((isSentTab.value ? (props.row as SentEmailRow)?.bodyText : (props.row as ThreadRow)?.latestMailboxEmail?.bodyText), true));
const avatarName = computed(() => isSentTab.value ? t('home.to', { name: (props.row as SentEmailRow)?.to[0].name ?? (props.row as SentEmailRow)?.to[0].email }) : (props.row as ThreadRow)?.receivingCreator?.name);

const moveBackToInbox = async (event: Event, uuid?: string) => {
  if (!uuid) return;
  event.stopPropagation();
  await threadRepository.moveBackToInbox(uuid);
  threadStore.clearState();
  await threadStore.getThreadList();
  toast.success(t('toasters.moveBackToInbox'));
};
</script>

<template>
  <div
    class="thread-row"
    :class="{ 'unread': (row && 'read' in row) && !row?.read && !isSentTab, 'sent-tab': isSentTab, 'trash-tab': isTrashTab }"
  >
    <div class="thread-cell">
      <Avatar
        :avatar-url="(row && 'receivingCreator' in row) ? row?.receivingCreator?.avatarUrl : undefined"
        :name="avatarName"
        :hide-initials="isSentTab"
        small
      />
    </div>
    <div class="thread-cell">
      <div
        v-if="!isSentTab"
        class="number"
      >
        {{ (row && 'emailsCount' in row) ? row?.emailsCount : '' }}
      </div>
      <div class="flex-shrink">
        {{ row?.subject }}
      </div>
      <div>-</div>
      <div class="ellipsis-text">
        {{ messageBodyText }}
      </div>
    </div>
    <div class="thread-cell thread-cell--right no-padding">
      {{ messageDate }}
    </div>
    <div
      v-if="isTrashTab"
      class="thread-cell thread-cell--right no-padding trash-icon"
    >
      <IconMoveToInbox
        v-tooltip="$t('tooltips.moveBackToInbox')"
        @click="moveBackToInbox($event, row?.uuid)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.thread-row {
  display: grid;
  grid-template-columns: 320px calc(100vw - 660px) 140px;
  border-top: 1px solid var(--border-default);
  background-color: var(--white);
  padding: 0 30px;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: var(--gray);
  }

  &:first-of-type {
    border-top: none;
  }

  &.unread {
    background-color: var(--blue-highlight);

    .number {
      background-color: var(--blue-light);
      color: var(--blue-darkest);
    }
  }

  &.sent-tab {
    grid-template-columns: 320px calc(100vw - 660px) 140px;
  }

  &.trash-tab {
    &:hover {
      grid-template-columns: 320px calc(100vw - 710px) 140px 50px;

      .trash-icon {
        display: flex;
      }
    }
  }
}

.thread-cell {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;

  &--center {
    justify-content: center;
  }

  &--right {
    justify-content: right;
  }

  &.trash-icon {
    display: none;
  }
}

.number {
  background-color: var(--gray-dark);
  padding: 2px 8px;
  border-radius: 4px;
}

.flex-shrink {
  flex-shrink: 1;
}

.no-padding {
  padding: 0;
}
</style>
